import { NgTemplateOutlet } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { SlModalComponent, SlModalService } from '@sl/sl-angular-library';
import { Observable, forkJoin, map, switchMap } from 'rxjs';
import { EImages } from 'src/app/core/enums/images.enum';
import { RoleGroup } from 'src/app/core/enums/role-groups';
import { ERole } from 'src/app/core/enums/roles.enum';
import { ApplicationWindowService } from 'src/app/core/services/application-window.service';
import { PeopleService } from 'src/app/core/services/people.service';
import { RoleService } from 'src/app/core/services/roles.service';
import { EApplicationStatus } from 'src/app/shared/enums/application-status.enum';
import { IApplicationWindow } from 'src/app/shared/models/application-window.model';
import { IApplication } from 'src/app/shared/models/application.model';
import { IUserProfile } from 'src/app/shared/models/user-profile.model';
import { IViewableAcademicYears } from 'src/app/shared/models/viewable-academic-years.model';
import { NotificationComponent } from '../../shared/components/notification.component/notification.component';
import { StatusDisplayComponent } from '../../shared/components/status-display/status-display.component';
import { ILeadershipPosition } from 'src/app/shared/models/leadership-position.model';
import { ApplicationService } from 'src/app/core/services/application.service';
import { OfferService } from 'src/app/core/services/offer.service';
import { IOffer } from 'src/app/shared/models/offer.model';

@Component({
  selector: 'app-student-landing-page',
  templateUrl: './student-landing-page.component.html',
  styleUrls: ['./student-landing-page.component.scss'],
  imports: [NotificationComponent, RouterLink, NgTemplateOutlet, StatusDisplayComponent]
})
export class StudentLandingPageComponent implements OnInit {
  public windowClosedModal: SlModalComponent;

  protected welcomeMessage = "Thank you for helping to fulfill God's calling by praying for and serving your fellow students."
  protected welcomeMessageNS = "Help fulfill God's calling by praying for and serving your fellow students."
  public welcomeImg: string;

  protected rsImg = EImages.RS;
  protected csImg = EImages.CS;
  protected asImg = EImages.AS;
  protected cglImg = EImages.CGL;

  protected isShepherd = false;
  protected profile: IUserProfile;

  protected currentApplicationWindow: IApplicationWindow;
  protected nextApplicationWindow: IApplicationWindow;
  protected isRolloverActive = false;
  public showRolloverNotification = false;
  public showOffersNotification = false;

  public isFutureRS = false;
  public isRS = false;

  public offers: IOffer[] = [];

  protected cards: Array<IApplicationWindow | IApplication>;

  protected currentPosition: ILeadershipPosition;

  constructor(
    private roleService: RoleService,
    private applicationWindowService: ApplicationWindowService,
    private applicationService: ApplicationService,
    private router: Router,
    private slModalService: SlModalService,
    private peopleService: PeopleService,
    private offerService: OfferService,
  ) { }

  ngOnInit(): void {
    this.isShepherd = this.roleService.hasAnyRole(RoleGroup.STUDENT_SHEPHERDS);
    this.isFutureRS = this.roleService.hasRole(ERole.FUTURE_RS);
    this.isRS = this.roleService.hasRole(ERole.CURRENT_RS);

    this.welcomeImg = this.getWelcomeImg();

    this.setupApplicationCards();

    this.checkForOffers();

    const currentApplicationWindowObserver: Observable<Array<IApplicationWindow>> = this.applicationWindowService.getActiveApplicationWindows("RS");
    const withinRolloverPeriod: Observable<boolean> = this.applicationService.canRollover();

    forkJoin([currentApplicationWindowObserver, withinRolloverPeriod])
      .subscribe(([applicationWindows, withinRolloverPeriod]) => {
        if (applicationWindows && applicationWindows.length) {
          this.currentApplicationWindow = applicationWindows[0];
          this.nextApplicationWindow = applicationWindows[1];
        }

        if (withinRolloverPeriod && !this.rolloverAlreadyDismissed()) {
          this.showRolloverNotification = true;
        }
      });
  }


  private checkForOffers(): void {

    this.applicationService.getPendingApplications().pipe(
      switchMap(pendingApps =>
        forkJoin(
          pendingApps.map(app =>
            this.offerService.getPendingOffersByApplication(app.applicationId)
          )
        )
      ),
      map(allOffers => allOffers.flat())
    ).subscribe(offers => {
      this.offers = offers;
      if (offers?.length && !this.offerAlreadyDismissed()) {
        this.showOffersNotification = true;
      }
    });

  }

  public applyNowAriaLabel(typeDescription: string): string {
    return "Apply Now for " + typeDescription;
  }

  public setupApplicationCards(): void {
    this.peopleService.getViewableYears().subscribe((awa: IViewableAcademicYears) => {
      // remove any applications that have been withdrawn, denied, not recommended, or expired
      const sortedApps = awa.applications
        .filter((app: IApplication) => app.status.statusCode !== EApplicationStatus.WITHDRAWN_BY_APPLICANT
          && app.status.statusCode !== EApplicationStatus.DENIED_BY_LU_SHEPHERD
          && app.status.statusCode !== EApplicationStatus.NOT_RECOMMENDED_BY_RS
          && app.status.statusCode !== EApplicationStatus.EXPIRED)
        .sort((a, b) => Number(a.acyrCode) - Number(b.acyrCode));

      // remove any application windows where there was an application that was denied or not recommended so they cannot apply again
      const deniedOrNotRecommededApps = awa.applications
        .filter((app: IApplication) => app.status.statusCode === EApplicationStatus.DENIED_BY_LU_SHEPHERD
          || app.status.statusCode === EApplicationStatus.NOT_RECOMMENDED_BY_RS
          || app.status.statusCode === EApplicationStatus.EXPIRED)
        .sort((a, b) => Number(a.acyrCode) - Number(b.acyrCode));
      let applicationWindows = awa.applicationWindows.filter(aw =>
        !deniedOrNotRecommededApps.some((a) => a.applicationType === aw.applicationType && a.acyrCode === aw.academicYear.acyrCode));

      // Remove any application window where an application already exists for the year and type.
      applicationWindows = applicationWindows.filter(appWindow =>
        !sortedApps.some(app =>
          appWindow.academicYear.acyrCode === app.acyrCode &&
          appWindow.applicationType === app.applicationType
        )
      );

      // cards will be ordered by application windows / available positions followed by current and future applications that have been submitted regardless if outside their application window
      this.cards = [...applicationWindows, ...sortedApps];
    });
  }

  onApplyClicked(applicationType: string): void {
    this.applicationWindowService.getActiveApplicationWindows(applicationType).subscribe(appWindow => {
      const currentDate = new Date();
      const openWindows = appWindow.filter(aw => {
        if (aw.openDate && aw.closeDate) {
          return currentDate >= new Date(aw.openDate) && currentDate <= new Date(aw.closeDate)
        }

        return false;
      });

      if (openWindows.length > 0) {
        this.router.navigate(['apply', applicationType.toLowerCase()]);
      } else {
        this.openWindowClosedModal();
      }
    });
  }

  dismissNotification() {
    const offerIds = this.offers.map(o => o.offerId).join(',');
    this.showOffersNotification = false;
    if (localStorage.getItem('dismissed-offers')) {
      const hasOffer = offerIds.split(',').includes(String(offerIds));
      if (!hasOffer) {
        const newOffers = localStorage.getItem('dismissed-offers') + offerIds
        localStorage.setItem('dismissed-offers', newOffers)
      }
    } else {
      localStorage.setItem('dismissed-offers', offerIds)
    }

    this.showRolloverNotification = false;
    if (this.currentApplicationWindow?.academicYear) {
      localStorage.setItem('rollover-' + this.currentApplicationWindow.academicYear.acyrCode, 'dismissed');
    }
  }

  offerAlreadyDismissed(): boolean {
    const offerIds = this.offers.map(o => o.offerId).join(',');
    if (localStorage.getItem('dismissed-offers')) {
      return offerIds.split(',').includes(String(offerIds));
    } else {
      return false;
    }
  }

  rolloverAlreadyDismissed(): boolean {
    return localStorage.getItem('rollover-' + this.currentApplicationWindow.academicYear.acyrCode) ? true : false;
  }

  followRoute() {
    this.router.navigate(['/profile']);
  }

  public formattedYear(year: string, numToAdd: number): number {
    return Number(year) + numToAdd;
  }

  public getWelcomeImg(): string {
    if (this.roleService.hasRole(ERole.CURRENT_RS) || this.roleService.hasRole(ERole.FUTURE_RS)) {
      return EImages.NS;
    } else {
      return EImages.NS;
    }
  }

  public openWindowClosedModal() {
    this.windowClosedModal = this.slModalService.createModal({
      title: "Application Window Closed",
      message: "Applications are no longer being accepted. Please contact the Office of Spiritual Development to be considered for a position.",
      class: 'error-modal',
    })
  }

  public getCards(): Array<IApplicationWindow | IApplication> {
    return this.cards;
  }

}
